import {observer} from "mobx-react-lite";
import {useContext, useEffect, useRef} from "react";
import {reaction, comparer, runInAction} from "mobx";
import {AdminStore} from "./stores/admin_store";
import {AdminStoreContext} from "./protected_route";
import {useUrlState, calendarUrlSchema, decode} from "./stores/admin_url_state";
import {AdminCalendars} from "./components/admin_calendars";
import styled, {useTheme} from "styled-components";
import {AdminButton, AdminScaffold} from "./admin_scaffold";
import {CvvRoutes} from "../strings";
import {AdminHeader} from "./components/admin_decorations";
import {AdminTecaji} from "./components/admin_tecaji";
import {DateFormatters} from "../utils";
import {Theme} from "../theme";
import {useNavigate} from "react-router-dom";
import {cvv_types} from "../proto/compiled";


const Container = styled.div`
  flex-direction: column;
  display: flex;
  align-items: center;
`;

const Select = styled.select`
  margin-right: 8px;
`;

const EmptyDayMessage = styled.div`
  width: 100%;
  padding: 16px;
  text-align: center;
  color: #666;
  font-style: italic;
`;

export const AdminRazpisaniTecaji = observer(() => {
    const store = useContext(AdminStoreContext) as AdminStore;
    const year = new Date().getFullYear();
    const theme = useTheme() as Theme;
    const navigate = useNavigate();
    const {params, setValues} = useUrlState(calendarUrlSchema);
    const hydrated = useRef(false);

    // URL -> store, ONCE on mount (refresh / shared link). The calendar owns a lot of
    // interactive state (3 controllers, in-calendar month arrows, day toggling), so we
    // don't force every handler through the URL; we hydrate once here and mirror back
    // via the reaction below. Existing handlers stay untouched.
    useEffect(() => {
        if (hydrated.current) return;
        hydrated.current = true;
        const v = decode(params, calendarUrlSchema);
        runInAction(() => {
            store.tecajTip = v.tip;
            store.tecaj = v.tecaj;
            if (v.month) {
                const [y, m] = v.month.split('-').map(Number);
                store.calendars.setYear(y);
                store.calendars.setMonth(m - 1);          // schema is 1-based, controller 0-based
            }
            // No day is selected unless the URL says so (refresh / shared link): the page
            // opens on the whole month and a day is chosen only by clicking it.
            if (v.dan) {
                const [y, m, d] = v.dan.split('-').map(Number);
                store.calendars.selectedDate = new Date(y, m - 1, d);
            } else {
                store.calendars.selectedDate = undefined;
            }
        });
        store.api.connection.whenConnected.then(() => store.calendars.load());
    }, [store, params]);

    // store -> URL, on every tracked change (replace: no history spam). Captures month
    // changes from either the <Select> or the in-calendar arrows, and day selection from
    // the nested calendar component, without touching any of those call sites.
    useEffect(() => reaction(
        () => ({
            tip: store.tecajTip,
            tecaj: store.tecaj,
            month: `${store.calendars.controllers[1].year}-${String(store.calendars.controllers[1].month + 1).padStart(2, '0')}`,
            dan: store.calendars.selectedDate ? DateFormatters.isoDate(store.calendars.selectedDate) : undefined,
        }),
        (v) => setValues(v, {replace: true}),
        {equals: comparer.structural},
    ), [store, setValues]);

    return <AdminScaffold route={CvvRoutes.admin.razpisaniTecaji}>
        <Container>
            <AdminHeader>
                <Select value={store.tecajTip ?? ''}
                        onChange={(e) => store.setTecajTip(e.target.value)}>
                    <option value={''}>---- izberi tip ----</option>
                    {store.meta?.tecajTipi?.map((t: cvv_types.IChoice) => <option key={t.text!}
                                                                                  value={t.text!}>{t.name}</option>)}
                </Select>
                <Select value={store.tecaj ?? ''} onChange={(e) => {
                    store.setTecaj(e.target.value);
                    store.calendars.load();
                }}>
                    <option value={''}>---- izberi tecaj ----</option>
                    {store.meta &&
                        store.meta?.tecajTipTecaji![store.tecajTip!]?.choices?.map(
                            (t: cvv_types.IChoice) => <option key={t.text!} value={t.text!}>{t.name}</option>
                        )}
                </Select>
                <AdminButton
                    onClick={() => {
                        navigate(CvvRoutes.admin.razpisaniTecaj());
                    }}
                    style={{marginLeft: 'auto'}}>
                    + DODAJ NOV TERMIN
                </AdminButton>
            </AdminHeader>

            <AdminHeader style={{marginTop: '8px', justifyContent: 'center'}}>
                <theme.textTheme.button.span style={{
                    position: 'absolute',
                    left: '16px',
                    color: 'white',
                }}>KOLEDAR
                </theme.textTheme.button.span>
                <Select value={store.calendars.controllers[1].month}
                        onChange={(e) => store.calendars.setMonth(parseInt(e.target.value))}>
                    {DateFormatters.months.map((m, i) => <option key={i} value={i}>{m}</option>)}
                </Select>
                <Select value={store.calendars.controllers[1].year}
                        onChange={(e) => store.calendars.setYear(parseInt(e.target.value))}>
                    {new Array(10).fill(0).map((m, i) => <option key={i} value={year + i - 8}>{year + i - 8}</option>)}
                </Select>
            </AdminHeader>
            <div style={{padding: '8px'}}>
                <AdminCalendars/>
            </div>

            <AdminHeader style={{marginTop: '8px'}}>
                <theme.textTheme.button.span style={{color: 'white', marginRight: 'auto'}}>
                    RAZPISANI TEČAJI {store.calendars.rangeText()}
                </theme.textTheme.button.span>
            </AdminHeader>
            {store.calendars.tipi.map((tip) => <AdminTecaji key={tip} tip={tip}/>)}
            {store.calendars.tipi.length === 0 && !store.loading &&
                <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
                    <EmptyDayMessage>
                        {store.calendars.selectedDate
                            ? 'Za izbrani dan ni razpisanih tečajev.'
                            : 'Za izbrani mesec ni razpisanih tečajev.'}
                    </EmptyDayMessage>
                    {/* Gumb je smiseln samo, kadar je izbran dan - brez izbire smo že na mesecu. */}
                    {store.calendars.selectedDate && <AdminButton
                        style={{color: theme.content, borderColor: theme.primaryDark}}
                        background={theme.primary}
                        backgroundHover={'#ffe871'}
                        onClick={() => {store.calendars.selectDate(store.calendars.selectedDate!)}}>
                        PRIKAŽI VSE TERMINE ZA TRENUTNI MESEC</AdminButton>}
                </div>}
        </Container>
    </AdminScaffold>
});